import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router';

import {
  Content,
  Header,
  InfoCard,
  Page,
  Progress,
} from '@backstage/core-components';
import { identityApiRef, useApi } from '@backstage/core-plugin-api';

import Typography from '@material-ui/core/Typography';

export type Status =
  | { kind: 'loading' }
  | { kind: 'invalid-callback'; reason: string }
  | { kind: 'missing-state' }
  | { kind: 'no-token' }
  | { kind: 'send-failed'; reason: string }
  | { kind: 'done' };

const ALLOWED_HOSTS = new Set(['127.0.0.1', 'localhost']);

export const isCallbackUrlAllowed = (raw: string | null): boolean => {
  if (!raw) return false;
  let parsed: URL;
  try {
    parsed = new URL(raw);
  } catch {
    return false;
  }
  if (parsed.protocol !== 'http:') return false;
  if (!ALLOWED_HOSTS.has(parsed.hostname)) return false;
  return true;
};

interface MinimalIdentityApi {
  getCredentials(): Promise<{ token?: string }>;
}

interface DeliverTokenToCliOptions {
  search: string;
  identityApi: MinimalIdentityApi;
  fetchImpl?: typeof fetch;
}

export const deliverTokenToCli = async ({
  search,
  identityApi,
  fetchImpl,
}: DeliverTokenToCliOptions): Promise<Status> => {
  const params = new URLSearchParams(search);
  const callback = params.get('callback');
  const state = params.get('state');

  if (!isCallbackUrlAllowed(callback)) {
    return {
      kind: 'invalid-callback',
      reason:
        'The callback URL must be a loopback address (http://127.0.0.1 or http://localhost).',
    };
  }
  if (!state) {
    return { kind: 'missing-state' };
  }

  const errMessage = (err: unknown): string =>
    err instanceof Error ? err.message : String(err);

  let token: string | undefined;
  try {
    const creds = await identityApi.getCredentials();
    token = creds.token;
  } catch (err) {
    return { kind: 'send-failed', reason: errMessage(err) };
  }

  if (!token) {
    return { kind: 'no-token' };
  }

  const doFetch = fetchImpl ?? globalThis.fetch;
  try {
    const response = await doFetch(callback as string, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token, state }),
    });
    if (!response.ok) {
      const detail = await response.text().catch(() => '');
      return {
        kind: 'send-failed',
        reason: `loopback returned ${response.status} ${response.statusText}${
          detail ? `: ${detail}` : ''
        }`,
      };
    }
    return { kind: 'done' };
  } catch (err) {
    return { kind: 'send-failed', reason: errMessage(err) };
  }
};

interface CliAuthPageProps {
  fetchImpl?: typeof fetch;
}

export const CliAuthPage: React.FC<CliAuthPageProps> = ({ fetchImpl }) => {
  const location = useLocation();
  const identityApi = useApi(identityApiRef);
  const [status, setStatus] = useState<Status>({ kind: 'loading' });

  useEffect(() => {
    let cancelled = false;
    deliverTokenToCli({
      search: location.search,
      identityApi,
      fetchImpl,
    }).then(result => {
      if (!cancelled) setStatus(result);
    });
    return () => {
      cancelled = true;
    };
  }, [location.search, identityApi, fetchImpl]);

  return (
    <Page themeId="tool">
      <Header
        title="Frontstage CLI sign-in"
        subtitle="Hand the session token back to the local CLI"
      />
      <Content>
        <InfoCard title="CLI authentication">
          {status.kind === 'loading' && (
            <>
              <Progress />
              <Typography>Signing you in to the CLI…</Typography>
            </>
          )}
          {status.kind === 'invalid-callback' && (
            <Typography color="error">
              Refused to send the token: {status.reason}
            </Typography>
          )}
          {status.kind === 'missing-state' && (
            <Typography color="error">
              Refused to send the token: the request is missing a state
              parameter.
            </Typography>
          )}
          {status.kind === 'no-token' && (
            <Typography color="error">
              Could not obtain an identity token. Sign in and try again.
            </Typography>
          )}
          {status.kind === 'send-failed' && (
            <Typography color="error">
              Failed to deliver the token to the CLI: {status.reason}
            </Typography>
          )}
          {status.kind === 'done' && (
            <Typography>You can close this tab.</Typography>
          )}
        </InfoCard>
      </Content>
    </Page>
  );
};
